import type { NpcHullTier, PlayerShip } from "../types";
import type { MissionType } from "../missionBoard";
import type { AcceptedMission } from "../acceptedMissions";
import type { CombatSnapshot } from "../combatSnapshot";
import { initialClockState, type ClockState } from "../gameClock";
import type { BaseRecord } from "../baseStorage";
import type { NodeRegenRecord } from "../resourceNodeRegen";
import type { FactionStanding } from "../factionStanding";
import { initialStanding } from "../factionStanding";
import { createCaptainSkills, type CaptainSkillXp } from "../captainSkills";
import { createCaptainStats, type CaptainStats } from "../captainStats";
import type { WeaponUpgrades } from "../weaponUpgrades";
import type { ShipDamageMap } from "../shipDamage";
import type { BestiaryRecord } from "../bestiary";
import type { GameSaveData } from "../gameSave";
import { settings, updateSettings } from "../settings";

export type Screen =
  | "checking"
  | "mainMenu"
  | "login"
  | "ships"
  | "board"
  | "diary"
  | "expedition"
  | "spaceMap"
  | "cargo"
  | "shipyard"
  | "fleet"
  | "lounge"
  | "trophies"
  | "market"
  | "base"
  | "station"
  | "options"
  | "shipCard"
  | "escapePod"
  | "welcome"
  | "faq"
  | "marketOverview"
  | "saveCompare";
export type AuthStatus = "checking" | "authenticated" | "unauthenticated";

// Section 10's Escort Autonomy & Fleet Management UI (LOCKED fields).
// Additive to `currentShip` -- currentShip stays exactly what it always
// was (the ship actively being flown, read everywhere from the
// Expedition View to Mission Board's DPS calc), never replaced by this.
// A ship enters `fleet` either "owned" (bought at the Shipyard) or
// "hired" (rented at a Station's Shipyard since 16.76, Section 10) -- both
// live in the same list, tagged, per the doc's own roster shape.
// `stance` (Active/Passive) has a real behavioral effect now (16.43):
// every "Active" ship other than whichever is being flown joins the
// Expedition View as a real ship (own hull/shield/dps, auto-targeting the
// nearest hostile in its own detection range); "Passive" ships simply
// don't join the scene at all.
export interface FleetShip {
  id: string;
  ship: PlayerShip;
  source: "owned" | "hired";
  stance: "Active" | "Passive";
  // Ongoing hired-ship upkeep (16.54) -- 5% of Shipyard cost per real-
  // clock-hour, replacing the old one-time 25% hire fee. Undefined for
  // "owned" ships (owned-ship upkeep stays food/fuel only, Section 10).
  // effectiveNow-based epoch ms of the last hour this ship was actually
  // billed through -- advanced by billHiredShipUpkeep each time it bills,
  // never by hire time alone, so re-billing is always "elapsed since last
  // bill," not "elapsed since hire."
  lastUpkeepAt?: number;
  // Last Player-Owned Base this ship was actually docked at
  // (expeditionScreen.ts's tryDock), keyed the same way as
  // state.ownedLeases/BaseRecord ("<systemId>:<planetId>"). Every ship
  // must track this (16.62) so a provisions-mutiny has somewhere real to
  // return the ship+crew to. undefined until the ship's first real dock.
  // Where this ship is physically stored: `"<systemId>:<locationId>"`
  // (src/shipStorage.ts). Generalized in 16.72 from the old
  // Base-only `dockedAtBaseKey` -- a Station is just the Location id
  // "station", so the key format itself didn't change, only its reach.
  // undefined until the ship's first real dock, and cleared while it's
  // the hull being flown.
  dockedAtKey?: string;
}

export interface AppState {
  screen: Screen;
  authStatus: AuthStatus;
  username: string | null;
  /**
   * What to actually show on screen, wherever the game used to print
   * `username` raw -- see ui/playerName.ts's formatPlayerName. Kept separate
   * from `username`, which stays the raw identifier a few call sites
   * genuinely need (e.g. as a login field default).
   */
  displayName: string | null;
  saveVersion: number;
  saveNotice: string | null; // one-time save notice (e.g. an unresolved conflict)
  // Live save conflict (16.69): set when the server rejects a save because
  // another session already advanced past our version. Rather than the old
  // silent server-wins overwrite, this parks the server's copy here and
  // sends the captain to Compare Saves to choose -- so neither save is
  // destroyed without them seeing both. Session-only, never persisted:
  // it's an unresolved decision, not progress.
  saveConflict: {
    serverState: GameSaveData;
    serverVersion: number;
    serverUpdatedAt: number | string | null;
  } | null;
  currentShip: PlayerShip | null;
  // Stable identity for whichever real ship instance is currently flown --
  // distinct from currentShip's name/variant, since two identically-named
  // ships in the fleet must be able to hold independent Armory weapon
  // upgrade levels (16.36). Reuses a FleetShip's own `id` when flying a
  // fleet ship; a fresh id otherwise (the one-off starter ship picked at
  // Ship Select, before any fleet entry exists). null only alongside a
  // null currentShip.
  currentShipInstanceId: string | null;
  // Per-ship-instance weapon upgrade levels (Armory, 16.36) -- see
  // src/weaponUpgrades.ts. Keeps the fixed 96-ship roster (16.16) exactly
  // as locked; this only modifies the three real combat weapons'
  // (Laser/Auto/Missile Turret) effective DPS on top of that.
  weaponUpgrades: WeaponUpgrades;
  // Persistent hull/shield damage per ship instance (16.81,
  // src/shipDamage.ts). Damage used to evaporate the moment the captain
  // left the Expedition View; it now survives travel, and only an
  // activated Shield/Hull module, a Station drydock, or a Base repair bay
  // undoes it. Keyed the same way weaponUpgrades is -- by instance id, so
  // two identically-named hulls stay independent.
  shipDamage: ShipDamageMap;
  // Which NPC opponents this captain has actually met, and how many of
  // each they've destroyed (16.82, src/bestiary.ts). Drives the Trophy
  // Room's bestiary -- unmet opponents stay locked silhouettes.
  bestiary: BestiaryRecord;
  // Hidden systems whose discovery paragraph (16.82) has already been
  // shown, so the full-screen reveal fires exactly once per system even
  // though `visitedSystemIds` is written at the same moment.
  loreSeenSystemIds: string[];
  currentSystemId: string;
  currentLocationId: string;
  // Founded/leased planets (Don's direction: "price of a lease should be
  // set to 10,000 credits for now" -- grants Dock/Base View access at that
  // planet). Keyed "<systemId>:<planetId>", never bare planet id -- planet
  // ids are only unique within a System (16.35's "planet-1"/"planet-2"
  // convention), so the system id must be part of the key.
  ownedLeases: string[];
  // One real Base record per owned lease (src/baseStorage.ts), created the
  // instant a lease is purchased (expeditionScreen.ts's Buy Lease action).
  bases: BaseRecord[];
  // Persisted per-node regen state (16.53) -- one record per belt/mining
  // asteroid node ever visited (src/resourceNodeRegen.ts), keyed
  // `<systemId>:<locationId>:<nodeId>` since node ids ("ast1"/"ast2") are
  // reused across every belt/mining Location in the galaxy. A node not
  // yet in this list simply hasn't been visited/mined yet -- it starts
  // full the first time it is.
  nodeRegen: NodeRegenRecord[];
  // Faction Standing (16.48) -- one numeric value per real faction plus
  // Pirates, -10..+10, starts at 0 for all four (src/factionStanding.ts).
  standing: FactionStanding;
  // Captain Skills (16.19/16.31, finally wired in 16.69). Accumulated XP
  // per skill -- levels/bonuses are always derived from this via
  // captainSkills.ts, never stored, so the curve stays a single source of
  // truth. Belongs to the CAPTAIN, not the ship: surviving a hull swap or
  // an Escape Pod ejection is the whole point of a skill system that
  // "reflects how a captain spends their time" (16.19).
  captainSkills: CaptainSkillXp;
  // Lifetime career stats (16.76) -- what the Captain's Lounge reports
  // back to the captain. Deliberately raw counters incremented at the
  // exact points the real events already resolve, never derived after the
  // fact, so they can't drift from what actually happened. Persisted:
  // a career total that reset every session would be meaningless.
  stats: CaptainStats;
  // Which of the three top-level "camera" screens (Section 2a) the ship
  // was physically in when the browser was last closed/logged out --
  // login/Continue lands here instead of the Mission Board (Don's
  // direction, 16.35). null only pre-first-ship (Ship Select hasn't run
  // yet, nothing to resume into).
  lastTopLevelView: "expedition" | "base" | "station" | null;
  // Real game clock (16.34/16.36) -- see src/gameClock.ts. Persisted so a
  // real pause span (logged out, or docked at a Base/Station) survives a
  // page reload rather than resetting.
  clock: ClockState;
  // Fuel pool for the currently-flown ship (16.50) -- refilled to
  // `fuelCapacity` whenever the flown ship instance changes (fleet
  // switch, Shipyard buy-and-fly, Ship Select), persisted otherwise so
  // it survives reload/logout like credits/cargo. Gates Fast Travel/Warp
  // in expeditionScreen.ts, never anything else.
  fuelOnBoard: number;
  fuelCapacity: number;
  // Provisions pool for the currently-flown ship (Section 11/16.62) --
  // same refill-on-ship-switch convention as Fuel above. Consumed against
  // the PAUSABLE game clock (effectiveNow), unlike Fuel -- see
  // src/provisions.ts.
  provisionsOnBoard: number;
  provisionsCapacity: number;
  // effectiveNow epoch ms this ship's Provisions were last actually
  // ticked down through -- advanced only when consumeProvisions() runs,
  // same "elapsed since last tick" convention as FleetShip.lastUpkeepAt.
  provisionsLastUpdatedAt: number;
  // Mutiny countdown (16.62) -- effectiveNow epoch ms Provisions first
  // hit 0, or null if not currently depleted (resupplying before the
  // 20-minute deadline resets this to null). mutinyWarningsIssued (0-3)
  // tracks which 5-minute narrative warning was last shown, so the same
  // one never fires twice.
  provisionsDepletedAt: number | null;
  mutinyWarningsIssued: number;
  // Escape Pod (16.62) -- true once the captain has been ejected by
  // mutiny (or is otherwise shipless-and-podded) and hasn't yet picked up
  // a Loaner Ship or reclaimed an owned one. Real game state, persisted:
  // a captain who closes the tab mid-Pod segment must resume podded, not
  // lose the state.
  inEscapePod: boolean;
  // Loaner Ship (16.63) -- true while `currentShip` is the Faction-
  // Missions-only loaner picked up from a Station. Gates Cargo/Sell
  // access and the Mission Board's own faction-only filter, and the 30%
  // reward multiplier at mission completion.
  isLoanerShip: boolean;
  credits: number;
  cargo: Record<string, number>; // general cargo hold, resource name -> m3
  acceptedMissions: AcceptedMission[];
  waypointSystemId: string | null;
  // The fight in progress, if any (16.99). Kept on state rather than inside the
  // Expedition View so toSaveData can see it -- the View owns writing it.
  combatSnapshot: CombatSnapshot | null;
  fleet: FleetShip[];
  lastEvent: string | null; // short, transient feedback banner
  // Whether the new-captain welcome + tutorial has been dismissed. Persisted
  // so it appears once per captain rather than once per session.
  hasSeenWelcome: boolean;
  // 16.92: units the captain has already bought out of a station's DERIVED
  // stock. Keyed "epoch|stationId|itemName", so an entry from a previous
  // market hour identifies itself and is dropped on load -- the save only ever
  // carries what was traded in the current hour, and the 434-entry price table
  // itself is never stored at all. This ledger is the ONLY market state that
  // needs saving, which is the whole payoff of deriving prices.
  stationPurchases: Record<string, number>;
  // 16.95: which (epoch, faction) pairs have already been credited the trade
  // standing nudge. Same self-cleaning epoch key as stationPurchases -- a
  // reputation award that could be repeated within the hour would be farmable,
  // and one that never expired would grow without bound.
  tradeStandingAwarded: Record<string, number>;
  roleFilter: string | "All";
  tierFilter: NpcHullTier | "All";
  boardFilter: MissionType | "All";
  // Dev-only convenience (Don's direction): shows/hides the full nav button
  // bar and other dev-only shortcuts (e.g. Mission Diary's instant-complete
  // button). Still NOT part of GameSaveData -- it's a device preference,
  // not captain progress -- but since 16.69 it IS persisted, in the
  // separate `starforge_settings` store (src/settings.ts) rather than
  // resetting every reload. Mirror any write through setDevDebug() below
  // so the two stay in sync.
  devDebugOn: boolean;
  // Local-first save (16.57) -- true once bootAuth finds a save (local OR
  // server) to resume from, even before a ship has been picked from it.
  // Session-only, NOT part of GameSaveData/persistence: it describes "was
  // there something to resume," re-derived fresh every boot, not durable
  // game progress itself.
  hasExistingSave: boolean;
  // Distress Signal (16.54) -- a real rescue mechanic for a ship stranded
  // with insufficient fuel to Fast Travel/Warp anywhere. Deliberately
  // session-only, NOT part of GameSaveData/persistence (same convention as
  // devDebugOn/shipCardTarget): being stranded is tied to a live Expedition
  // View session, not durable progress worth surviving a reload -- a
  // reloaded/returning captain simply re-sends the signal if still
  // stranded.
  distressSignal: {
    active: boolean;
    nextCheckAt: number | null; // effectiveNow-based epoch ms
    markup: number | null; // rolled once a check succeeds (3-5x), null while still waiting
  };
  // Space Map scanner-tier gating (16.46). Ordinary tier-gated visibility
  // (computeVisibility, galaxyVisibility.ts) is recomputed on demand from
  // this plus the current ship's real hull tier -- only the fact of
  // having visited a system needs to persist, not the derived visibility
  // itself. The 6 Safe Zone (dangerTier 0) systems are always "scanned"
  // without needing to be listed here (computeVisibility treats them as
  // roots directly).
  visitedSystemIds: string[];
  // Which of the 10 real `hiddenUntilScanned` systems (Section 6,
  // galaxyVisibility.ts's HIDDEN_SYSTEM_IDS) have had their neighbor-gate
  // detected via a scan roll, but not yet actually visited -- shows as a
  // "signal detected, unvisited" marker on the Space Map. A visited
  // hidden system moves to `visitedSystemIds` instead and no longer needs
  // an entry here.
  detectedHiddenSystemIds: string[];
  // Which ship the Ship Card pane (Section 12, 16.41) is currently showing
  // -- set by whichever screen's "View" action opened it (Fleet
  // roster/Shipyard browsing), read once the "shipCard" pane renders.
  // `instanceId` is null for a Shipyard browsing candidate that isn't
  // owned yet (no weapon-upgrade levels exist to show); real for any
  // fleet ship. Deliberately NOT part of GameSaveData/persistence -- same
  // session-only convention as devDebugOn, since this is just "what am I
  // currently looking at," not real game state.
  shipCardTarget: { ship: PlayerShip; instanceId: string | null } | null;
}

// S001 Stormpoint: the Safe Zone's hub system -- a sensible starting point
// for a brand-new player with no save yet, matching the design doc's own
// convention of anchoring worked examples there.
export const state: AppState = {
  screen: "mainMenu",
  authStatus: "checking",
  username: null,
  displayName: null,
  saveVersion: 0,
  saveNotice: null,
  saveConflict: null,
  currentShip: null,
  currentShipInstanceId: null,
  weaponUpgrades: {},
  shipDamage: {},
  bestiary: {},
  loreSeenSystemIds: [],
  currentSystemId: "S001",
  currentLocationId: "planet-1",
  ownedLeases: [],
  bases: [],
  nodeRegen: [],
  standing: initialStanding(),
  captainSkills: createCaptainSkills(),
  stats: createCaptainStats(),
  visitedSystemIds: [],
  detectedHiddenSystemIds: [],
  lastTopLevelView: null,
  clock: initialClockState(Date.now()),
  fuelOnBoard: 0,
  fuelCapacity: 0,
  provisionsOnBoard: 0,
  provisionsCapacity: 0,
  provisionsLastUpdatedAt: 0,
  provisionsDepletedAt: null,
  mutinyWarningsIssued: 0,
  inEscapePod: false,
  isLoanerShip: false,
  credits: 0,
  cargo: {},
  acceptedMissions: [],
  waypointSystemId: null,
  combatSnapshot: null,
  fleet: [],
  lastEvent: null,
  hasSeenWelcome: false,
  stationPurchases: {},
  tradeStandingAwarded: {},
  roleFilter: "All",
  tierFilter: "All",
  boardFilter: "All",
  devDebugOn: settings.devDebugOn,
  hasExistingSave: false,
  distressSignal: { active: false, nextCheckAt: null, markup: null },
  shipCardTarget: null
};

// Single write path for the DEV DEBUG toggle (16.69) -- keeps the live
// `state` flag every screen already reads and the persisted setting from
// drifting apart. Both toggle sites (the header's own dev button and the
// Options screen's checkbox) go through this.
export function setDevDebug(on: boolean): void {
  state.devDebugOn = on;
  updateSettings({ devDebugOn: on });
}
